import { useCallback, useState } from 'react';
import { toast } from '@kmgestao/ui';

import {
  FormRenderer,
  useAutosave,
  useAutosaveSubmission,
  useSubmitSubmission,
} from '../../features/forms/index.js';
import type { FormSchema } from '../../features/forms/index.js';

import { OnboardingAutosaveBadge } from './OnboardingAutosaveBadge.js';

export interface OnboardingFormRuntimeProps {
  token: string;
  templateName: string;
  schema: FormSchema;
  initialAnswers: Record<string, unknown>;
}

/**
 * Runtime do formulário público — junta `FormRenderer` com auto-save
 * debounced (2s) e submit final via edge function
 * `submit-onboarding-answer`.
 *
 * **State management**: `answers` vive aqui; `FormRenderer` notifica
 * via `onChange`. O hook `useAutosave` observa e dispara a mutation
 * de autosave depois do debounce. Submit final descaracteriza o
 * auto-save (a mutation subsequente com `submitted` é terminal).
 *
 * **Após submit**: mostra toast de sucesso; o Page superior detecta
 * via refetch da query e roteia para `OnboardingSubmittedState` em
 * milissegundos. Não navegamos manualmente — deixamos o estado do
 * backend guiar a UI, consistente com como a tela reaparece em reload.
 */
export function OnboardingFormRuntime({
  token,
  templateName,
  schema,
  initialAnswers,
}: OnboardingFormRuntimeProps) {
  const [answers, setAnswers] = useState<Record<string, unknown>>(initialAnswers);
  const autosaveMutation = useAutosaveSubmission();
  const submitMutation = useSubmitSubmission();

  // Callback estável para o hook de auto-save — evita reinício do
  // timer a cada render (se passar inline, o useEffect do hook
  // dispara em loop).
  const handleAutosave = useCallback(
    async (value: Record<string, unknown>) => {
      await autosaveMutation.mutateAsync({ token, partial_answers: value });
    },
    [autosaveMutation, token],
  );

  const autosave = useAutosave({
    value: answers,
    onSave: handleAutosave,
    delayMs: 2000,
    skipInitial: true,
  });

  const handleSubmit = useCallback(
    async (finalAnswers: Record<string, unknown>) => {
      // Desliga o autosave ANTES de submeter. Sem isso, um timer de
      // debounce pendente pode disparar DEPOIS do submit e bater um
      // 409 `already_submitted` no token já fechado. Bug 2026-04-24.
      autosave.cancel();
      try {
        await submitMutation.mutateAsync({ token, answers: finalAnswers });
        toast.success('Respostas enviadas! A consultora vai preparar sua proposta.');
        // O onSuccess da mutation invalida `FORMS_KEYS.publicSubmission(token)`
        // → OnboardingFormPage refetch → detecta `submitted` → troca pra
        // OnboardingSubmittedState automaticamente. Lead não vê mais o form
        // depois do sucesso (antes continuava visível e ele clicava de novo).
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro desconhecido';
        // Tratamento especial pra 409 `already_submitted`: é um cenário
        // de "sucesso tardio" — a submissão foi aceita num click anterior,
        // só a UI não viu. Tratar como sucesso (refetch da query já
        // acontece via onSuccess da 1ª request, ou aqui como fallback
        // quando o 1º request perdeu a resposta mas gravou no server).
        if (msg.includes('already_submitted')) {
          toast.success('Respostas já enviadas! A consultora vai preparar sua proposta.');
          return; // Não repropaga — FormRenderer mantém tela sem erro.
        }
        toast.error('Não conseguimos enviar suas respostas', { description: msg });
        throw err; // Repropaga — FormRenderer mantém o botão "Enviar" reativo
      }
    },
    [submitMutation, token, autosave],
  );

  return (
    <div className="space-y-4">
      <header className="space-y-1 px-1">
        <h1 className="text-xl font-semibold text-foreground sm:text-2xl">
          {schema.title || templateName}
        </h1>
        {schema.description ? (
          <p className="text-sm text-muted-foreground">{schema.description}</p>
        ) : null}
      </header>

      <OnboardingAutosaveBadge state={autosave.state} error={autosave.error} />

      <FormRenderer
        schema={schema}
        initialAnswers={initialAnswers}
        onChange={setAnswers}
        onSubmit={handleSubmit}
        submitLabel="Enviar respostas"
      />
    </div>
  );
}
